import * as React from "react";
import { Stack } from "@fluentui/react";
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Field,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Tab,
  TabList,
  useRestoreFocusTarget,
  ProgressBar,
} from "@fluentui/react-components";
import type { SelectTabData, SelectTabEvent, TabValue } from "@fluentui/react-components";
import { tokens } from "@fluentui/react-theme";
import { CloseIcon } from "..";
import { MessageProps, modelNames } from "./types";
import { RobotIconDocument, RobotIconGPT, CopyIcon, ExpandIcon, ThumbUpIcon, ThumbDownIcon } from "..";
import Feedback from "./Feedback";
import { postFeedback } from "./utilities";

const useStyles = makeStyles({
  stack: { justifyContent: "normal" },
  stackItem: { maxWidth: "70%", marginLeft: "0.5rem", marginRight: "0.5rem" },
  initialItem: { marginLeft: "0.5rem", marginRight: "0.5rem", ...shorthands.padding("0 1rem") },
  bubbleArea: { display: "flex", flexDirection: "column", whiteSpace: "pre-wrap" },
  bubbleUsername: { ...shorthands.margin("0.1rem") },
  username: { alignSelf: "flex-end" },
  aiName: { alignSelf: "flex-start" },
  bubble: {
    ...shorthands.padding("1rem"),
    marginTop: "0.1rem",
    marginBottom: "0.5rem",
    ...shorthands.borderRadius("1rem"),
    wordWrap: "break-word",
    boxShadow: tokens.shadow8,
    display: "flex",
  },
  bubbleUser: { backgroundColor: tokens.colorNeutralBackground6 },
  bubbleAI: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderStyle("solid"),
    ...shorthands.borderWidth("1px"),
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  float: { float: "right" },
  floatButton: { ...shorthands.borderRadius("0.5rem") },
  msgText: {
    ...shorthands.margin("0"),
    ...shorthands.overflow("auto"),
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
  },
  dialogText: { ...shorthands.margin("1rem"), whiteSpace: "pre-wrap", wordWrap: "break-word" },
  dialogBar: { display: "flex", justifyContent: "space-between" },
  refButton: {
    color: tokens.colorPaletteBlueBorderActive,
    fontSize: tokens.fontSizeBase200,
    cursor: "pointer",
    marginBottom: 0,
    marginTop: "0.25em",
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
  },
  waitText: {
    marginRight: "75px",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
    animationDirection: "alternate",
    animationDuration: "1.5s",
    animationName: {
      from: { opacity: 1 },
      to: { opacity: 0.3 },
    },
  },
  actionButtonArea: {
    display: "flex",
    ...shorthands.gap("0.25em"),
  },
  // actionButton: {
  //   ":hover": { scale: "1.25" }
  // }
});

const MyMessageBubble = React.forwardRef(function MessageBubble(
  props: {
    messages: any[];
    inform?: (i: string) => void;
    error?: (i: string) => void;
    userInfo?: { email: string; token: string };
  },
  ref: React.Ref<HTMLDivElement>
) {
  const messagesList: MessageProps[] = props.messages;

  const [selectedValue, setSelectedValue] = React.useState<TabValue>("tab1");
  const [open, setOpen] = React.useState(false);
  const [dialogMessage, setDialogMessage] = React.useState<MessageProps | null>(null);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedValue(data.value);
  };

  const feedbackClose = () => {
    setDialogMessage(null);
    setOpen(!open);
  };

  const classes = useStyles();
  return (
    <Stack className={classes.stack}>
      {messagesList.map((message) => (
        <Stack.Item
          className={message.initialItem ? classes.initialItem : classes.stackItem}
          key={message.id}
          align={message.sender != "AI" ? "end" : "start"}
        >
          <div className={classes.bubbleArea}>
            <b
              className={mergeClasses(
                classes.bubbleUsername,
                message.sender == "AI" ? classes.aiName : classes.username
              )}
            >
              {message.sender == "AI" ? message.gptMode ? <RobotIconGPT /> : <RobotIconDocument /> : message.sender}
            </b>
            <div
              className={mergeClasses(classes.bubble, message.sender != "AI" ? classes.bubbleUser : classes.bubbleAI)}
            >
              <div className={classes.msgText}>
                {message.sender === "AI" && !message.loading && !message.initialItem ? (
                  <div className={classes.float}>
                    <Button
                      className={classes.floatButton}
                      icon={<ExpandIcon />}
                      appearance="transparent"
                      onClick={() => {
                        setOpen(true);
                        setDialogMessage(message);
                        setSelectedValue("tab1");
                      }}
                    />
                  </div>
                ) : null}
                {message.loading ? (
                  <Field
                    label={<span className={classes.waitText}>Thinking...</span>}
                    validationState="none"
                    size="large"
                  >
                    <ProgressBar />
                  </Field>
                ) : (
                  message.text
                )}
                {message.sender == "AI" &&
                  !message.gptMode &&
                  !message.loading &&
                  message.references &&
                  Object.keys(message.references).length > 0 &&
                  !message.initialItem && (
                    <p
                      className={classes.refButton}
                      {...restoreFocusTargetAttribute}
                      onClick={() => {
                        setOpen(true);
                        setDialogMessage(message);
                        setSelectedValue("tab2");
                      }}
                    >
                      See references
                    </p>
                  )}
              </div>
            </div>
            <div className={classes.actionButtonArea}>
              {message.desktop && message.sender === "AI" && !message.loading && (
                <Button
                  // className={classes.actionButton}
                  icon={<CopyIcon />}
                  size="small"
                  appearance="subtle"
                  onClick={() => navigator.clipboard.writeText(message.text)}
                />
              )}
              {message.sender === "AI" && !message.loading && (
                <>
                  <Button
                    // className={classes.actionButton}
                    icon={<ThumbUpIcon />}
                    size="small"
                    appearance="subtle"
                    onClick={async () => {
                      const response = await postFeedback(
                        {
                          response: message.text,
                          email: props.userInfo.email,
                          model: message.gptMode ? modelNames.baseModelName : modelNames.documentModelName,
                          conversationId: parseInt(message.conversationId),
                          rating: 5,
                          feedback: "User thumbs up",
                        },
                        props.userInfo.token
                      );
                      if (response.error) {
                        props.error(response.error);
                      } else {
                        props.inform(response.message);
                      }
                    }}
                  />
                  <Button
                    // className={classes.actionButton}
                    icon={<ThumbDownIcon />}
                    size="small"
                    appearance="subtle"
                    onClick={async () => {
                      const response = await postFeedback(
                        {
                          response: message.text,
                          email: props.userInfo.email,
                          model: message.gptMode ? modelNames.baseModelName : modelNames.documentModelName,
                          conversationId: parseInt(message.conversationId),
                          rating: 0,
                          feedback: "User thumbs down",
                        },
                        props.userInfo.token
                      );
                      if (response.error) {
                        props.error(response.error);
                      } else {
                        props.inform(response.message);
                      }
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </Stack.Item>
      ))}
      <Stack.Item>
        <div ref={ref}></div>
      </Stack.Item>
      <Dialog
        open={open}
        onOpenChange={(_event, data) => {
          // it is the users responsibility to react accordingly to the open state change
          setOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogContent>
              <div className={classes.dialogBar}>
                <TabList selectedValue={selectedValue} onTabSelect={onTabSelect}>
                  <Tab value="tab1">Text</Tab>
                  {dialogMessage && !dialogMessage.gptMode && <Tab value="tab2">References</Tab>}
                  <Tab value="tab3">Feedback</Tab>
                </TabList>
                <DialogTrigger disableButtonEnhancement>
                  <Button icon={<CloseIcon />} appearance="transparent" onClick={() => setDialogMessage(null)}></Button>
                </DialogTrigger>
              </div>
              <div className={classes.dialogText}>
                {dialogMessage && selectedValue === "tab1" && dialogMessage.text}
                {dialogMessage &&
                  selectedValue === "tab2" &&
                  (dialogMessage.references && Object.keys(dialogMessage.references).length > 0 ? (
                    <ul>
                      {Object.entries(dialogMessage.references).map(([title, url], index) => (
                        <li key={index}>
                          <a
                            style={{ overflowWrap: "break-word", marginBottom: "1em" }}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {isNaN(Number(title)) ? title : url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No Results</p>
                  ))}
                {dialogMessage && selectedValue === "tab3" && (
                  <Feedback
                    props={{
                      feedbackDetails: {
                        response: dialogMessage.text,
                        email: props.userInfo.email,
                        model: dialogMessage.gptMode ? modelNames.baseModelName : modelNames.documentModelName,
                        conversationId: parseInt(dialogMessage.conversationId),
                        token: props.userInfo.token,
                      },
                      informFunction: props.inform,
                      errorFunction: props.error,
                      closeFunction: feedbackClose,
                    }}
                  />
                )}
              </div>
            </DialogContent>
            <DialogActions>
              {dialogMessage && dialogMessage.desktop && selectedValue === "tab1" && (
                <Button appearance="primary" onClick={() => navigator.clipboard.writeText(dialogMessage.text)}>
                  Copy message text
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </Stack>
  );
});

export default MyMessageBubble;
